import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { requestMagicLink, getErrorCode } from '@/services/api';
import { useBrand } from '@/contexts/BrandContext';
import { getReturnUrl } from '@/utils';
import { Button, ErrorState } from '@airclou/ui';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export function LoginPage() {
  const { brand } = useBrand();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      return;
    }

    setState('submitting');
    setErrorCode(null);

    try {
      const returnTo = getReturnUrl();
      await requestMagicLink(email, returnTo);
      setState('success');
    } catch (error) {
      setState('error');
      setErrorCode(getErrorCode(error));
    }
  };

  if (state === 'success') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Check your email
          </h1>
          <p className="text-muted-foreground mb-6">
            We've sent a sign-in link to <strong>{email}</strong>. Click the link in the email to continue.
          </p>
          <p className="text-sm text-muted-foreground">
            Didn't receive the email?{' '}
            <button
              onClick={() => setState('idle')}
              className="text-primary hover:underline"
            >
              Try again
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Sign in
          </h1>
          <p className="text-muted-foreground">
            Enter your email address and we'll send you a sign-in link.
          </p>
        </div>

        {state === 'error' && (
          <div className="mb-6">
            <ErrorState
              description={
                errorCode === 'RATE_LIMITED'
                  ? 'Too many requests. Please wait a few minutes and try again.'
                  : errorCode === 'INVALID_EMAIL'
                  ? 'Please enter a valid email address.'
                  : 'Failed to send sign-in link. Please try again.'
              }
              action={
                <button
                  onClick={() => setState('idle')}
                  className="text-primary hover:underline"
                >
                  Try again
                </button>
              }
            />
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              autoFocus
              autoComplete="email"
              className="w-full h-12 px-4 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <Button
            type="submit"
            disabled={state === 'submitting'}
            className="w-full h-12"
          >
            {state === 'submitting' && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Send sign-in link
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/" className="text-primary hover:underline">
            Back to Help Center
          </Link>
        </p>
      </div>
    </div>
  );
}
